/**
 * Proforma Invoices · Feb 2026
 * Sales & Dispatch (domestic) and Exports & Dispatch (export) both use this
 * component. Pass `piType="domestic"` or `piType="export"` from the route.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import CancelWithReasonDialog from "@/components/CancelWithReasonDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileTextIcon, PlusIcon, PrinterIcon, PencilLineIcon, TrashIcon, ProhibitIcon,
  XCircleIcon, CheckCircleIcon, WarningIcon, EyeIcon, ArrowRightIcon, XIcon,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getUser } from "@/lib/secureStorage";
import { canSubmitDoc } from "@/lib/submitPerms";
import { useAuth } from "@/lib/auth";
import ApprovalControls from "@/components/ApprovalControls";
import DocumentInfo from "@/components/DocumentInfo";
import { DocDownloadButtons, downloadDoc, useDocStyles } from "@/components/DocDownloadActions";
import { fmtDateTimeIST, fmtDateIST } from "@/lib/datetime";

// Feb 2026 · Central Indian/USD formatting from `lib/uom`. `fmt` keeps
// the 2-dp INR shape used by domestic PIs; export USD PIs render via
// `fmtCurrency(v, "USD")` (4 dp, US commas).
import { fmtInr as _fmtInr, fmtCurrency as _fmtCurrency } from "@/lib/uom";
// Feb 2026 · Shared print entry point — PI / SO / PO all route their
// browser-print action through this so the visual template stays in
// perfect sync across the three documents.
import { printDocument } from "@/components/DocumentPrintTemplate";
const fmt = (v) => _fmtInr(v, { symbol: false });
const fmtCurrency = _fmtCurrency;
const STATUS_STYLE = {
  Draft: "border-zinc-500/40 text-zinc-300",
  Issued: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  Amended: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  Cancelled: "border-red-500/40 text-red-300 bg-red-500/5",
  Authorised: "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
  "Pending Authorisation": "border-amber-500/50 text-amber-300 bg-amber-500/10",
  Rejected: "border-red-500/50 text-red-300 bg-red-500/10",
};

// Sep 03, 2026 · Consolidated Status column. The list previously
// showed the raw `status` (Draft/Issued/Amended/Cancelled) alongside a
// separate `approval_status` chip in the Actions cell — which produced
// confusing "DRAFT + AUTHORISED" or "AMENDED + AUTHORISED" combinations.
// The effective label below resolves the two fields into a single,
// human-readable status. Authorisation always wins over the lifecycle
// field once granted (a Draft/Issued/Amended PI that's been signed off
// by Management prints and lists as "Authorised") — the tooltip still
// exposes the raw split for audit clarity.
//   Cancelled                    → Cancelled
//   approval_status Rejected     → Rejected
//   approval_status Pending      → Pending Authorisation
//   approval_status Authorised   → Authorised
//   status Issued / Amended      → as-is
//   fallback                     → Draft
const APPROVED_APPROVAL = new Set(["Authorized", "Authorised", "Approved"]);
const PENDING_APPROVAL  = new Set(["Pending Approval", "Pending Authorisation"]);

// DP-PI-AUTH-EDIT-LOCK · Phase 2 · Feb 2026 — Canonical Authorised-state
// predicate. Once a PI is Management-Authorised, direct Edit and Delete
// are locked in the UI (backend remains authoritative). Rejected PIs
// stay editable so the Reject → Correct → Resubmit → Authorise
// lifecycle still works.
function isAuthorisedPi(r) {
  return APPROVED_APPROVAL.has(r?.approval_status);
}

// DP-PI-PENDING-FREEZE · Phase 2 · Feb 2026 — MA-INV-04 (Pending Content
// Freeze). A Domestic Proforma Invoice that has been submitted for
// Management Authorisation must not silently change or be deleted while
// its `approval_status` is "Pending Approval". EPI (`pi_type == "export"`)
// is handled by the parallel `isPendingExportPi` predicate below.
// Backend remains authoritative; this predicate merely mirrors the guard
// in the UI so operators see a clear read-only state and cannot fire a
// rejected PUT.
function isPendingDomesticPi(r) {
  return (r?.pi_type || "domestic") === "domestic" && PENDING_APPROVAL.has(r?.approval_status);
}

// DP-EPI-PENDING-FREEZE · Phase 2 · Feb 2026 — MA-INV-04 · Export branch.
// NEW PARALLEL predicate adjacent to the sealed `isPendingDomesticPi`.
// An Export PI whose `approval_status` is "Pending Approval" must not
// be edited or deleted through the shared editor / list-row controls
// while Management is deciding. Backend remains authoritative.
function isPendingExportPi(r) {
  return (r?.pi_type || "").trim() === "export" && PENDING_APPROVAL.has(r?.approval_status);
}

function effectiveStatus(r) {
  if (r?.status === "Cancelled") return "Cancelled";
  const a = r?.approval_status;
  if (a === "Rejected")            return "Rejected";
  if (PENDING_APPROVAL.has(a))     return "Pending Authorisation";
  if (APPROVED_APPROVAL.has(a))    return "Authorised";
  return r?.status || "Draft";
}

// PI Register V1 · Jun 2026 — presentation-only helpers for the line-wise
// register. Lifecycle (status) is PRIMARY; authorization is SECONDARY.
// Business behaviour / effStatus gates are unchanged.
function piAuthLabel(a) {
  if (APPROVED_APPROVAL.has(a)) return "Authorised";
  if (PENDING_APPROVAL.has(a))  return "Pending Authorisation";
  if (a === "Rejected")         return "Rejected";
  return "Draft";
}
// Currency-aware Rate — INR 2 dp (₹22.50); foreign 4 dp with explicit code
// (USD 0.2450) so an export rate is never shown without currency context.
function fmtPiRate(v, ccy) {
  const n = Number(v) || 0;
  const c = String(ccy || "INR").toUpperCase();
  if (c === "INR") return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${c} ${n.toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}
// Currency-aware Value / Grand Total — 2 dp. INR ₹ prefix; foreign code prefix.
function fmtPiVal(v, ccy) {
  const n = Number(v) || 0;
  const c = String(ccy || "INR").toUpperCase();
  if (c === "INR") return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${c} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ProformaInvoices({ piType = "domestic" }) {
  const isExport = piType === "export";
  const [rows, setRows] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null); // {id, invoice_no}
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const user = getUser();
  // Aug 31, 2026 · Zero-Trust — swap the hardcoded role-set for a DB
  // permission check so any admin-created role (e.g. "SALES SUPERVISOR")
  // that holds `proforma_invoices:create/edit` sees the New / Edit /
  // Delete controls. Administrator always passes via hasPerm().
  const { hasPerm } = useAuth();
  const canEdit = user?.role === "Administrator"
    || hasPerm?.("proforma_invoices:create")
    || hasPerm?.("proforma_invoices:edit");

  // PI Register V1 · Jun 2026 — line-wise register column chooser + density.
  // Presentation-only. Persisted per PI type (domestic `pi` / export `epi`)
  // so Domestic and Export keep independent column/density preferences and
  // never overwrite the sealed SO/ESO keys.
  const tp = isExport ? "epi" : "pi";
  const PI_ALL_COLS = useMemo(() => [
    { k: "date",          label: "PI Date" },
    { k: "buyer",         label: "Customer" },
    { k: "item",          label: "Item No" },
    { k: "style",         label: "Style No" },
    { k: "article",       label: "Product Code" },
    { k: "qty",           label: "Quantity" },
    { k: "rate",          label: "Rate" },
    { k: "value",         label: "Value" },
    { k: "status",        label: "Status" },
    { k: "source",        label: isExport ? "Source ESO" : "Source SO" },
    { k: "currency",      label: "Currency" },
    { k: "buyer_po",      label: "Buyer PO No" },
    { k: "hsn",           label: "HSN" },
    { k: "description",   label: "Description" },
    { k: "discount",      label: "Discount %" },
    ...(isExport ? [] : [{ k: "gst", label: "GST Rate" }]),
    ...(isExport ? [{ k: "fx", label: "FX Rate" }, { k: "converted_eso", label: "Converted ESO" }] : []),
    { k: "grand_total",   label: "Grand Total" },
  ], [isExport]);
  const defaultCols = () => new Set(["date", "buyer", "item", "style", "article", "qty", "rate", "value", "status", "source", ...(isExport ? ["currency"] : [])]);
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const raw = localStorage.getItem(`mes.${tp}.register.cols.v1`); if (raw) return new Set(JSON.parse(raw)); } catch (e) { /* ignore */ }
    return defaultCols();
  });
  const [colsMenu, setColsMenu] = useState(false);
  const toggleCol = (k) => setVisibleCols(prev => {
    const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem(`mes.${tp}.register.cols.v1`, JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const resetCols = () => {
    const next = defaultCols(); setVisibleCols(next);
    try { localStorage.setItem(`mes.${tp}.register.cols.v1`, JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
  };
  const col = (k) => visibleCols.has(k);
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem(`mes.${tp}.register.density`) || "comfortable"; } catch { return "comfortable"; }
  });
  const setDensityPersisted = (d) => {
    setDensity(d);
    try { localStorage.setItem(`mes.${tp}.register.density`, d); } catch (e) { /* ignore */ }
  };
  const pad = density === "compact" ? "px-2 py-1.5" : "px-3 py-2.5";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proforma-invoices/?pi_type=${piType}`);
      setRows(data || []);
    } catch { toast.error("Failed to load Proforma invoices"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [piType]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!needle) return true;
      return [r.invoice_no, r.buyer?.name, r.source?.no, r.references?.customer_po_no]
        .filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [rows, q, statusFilter]);

  const openEdit = async (r) => {
    try {
      const { data } = await api.get(`/proforma-invoices/${r.id}`);
      setEditing(data); setEditorOpen(true);
    } catch { toast.error("Failed to open Proforma"); }
  };

  const printPdf = async (r) => {
    try {
      const res = await api.get(`/proforma-invoices/${r.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF failed"); }
  };

  // Feb 25, 2026 · Safari-safe browser print via shared DocumentPrintTemplate.
  const printHtml = (r) => printDocument({ kind: "pi", id: r.id });

  const cancel = async (r) => {
    const reason = window.prompt(`Cancel ${r.invoice_no}?\nReason (min 4 chars):`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason ≥ 4 chars"); return; }
    try {
      await api.post(`/proforma-invoices/${r.id}/cancel`, { reason: reason.trim() });
      toast.success(`${r.invoice_no} · cancelled`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const convertToEso = async (r) => {
    if (!window.confirm(`Convert Proforma ${r.invoice_no} to a Draft Export Sales Order?\n\nAll buyer, logistics, currency and line-item details will be copied. You can review and authorise the ESO afterwards.`)) return;
    try {
      const { data } = await api.post(`/proforma-invoices/${r.id}/convert-to-eso`);
      toast.success(`Created ${data.doc_no} (Draft) from ${r.invoice_no}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
  };

  const remove = async (r) => {
    const isDraft = r.status === "Draft";
    const label = r.invoice_no || `draft ${r.id.slice(0, 8)}`;
    if (isDraft) {
      if (!window.confirm(`Delete draft ${label}?`)) return;
    } else {
      const resp = window.prompt(`⚠ DELETE ${r.status} Proforma ${label}?\n\nDeleting a numbered Proforma breaks the FY sequence.\nType YES to confirm:`, "");
      if (!resp || resp.trim().toUpperCase() !== "YES") return;
    }
    try { await api.delete(`/proforma-invoices/${r.id}`); toast.success(`${label} · deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const testidPrefix = isExport ? "epi" : "pi";
  const totalColSpan = 2 + visibleCols.size; // PI No + Actions are pinned

  return (
    <div className="p-6 space-y-6" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <FileTextIcon size={26} weight="bold" className="text-[var(--accent)]" />
            {isExport ? "Export Proforma Invoices" : "Proforma Invoices"}
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            {isExport
              ? "Pre-shipment invoices raised against Export SOs — used for LC / advance payment / customs paperwork."
              : "Preliminary invoices raised against Sales Orders — used for buyer advance / order confirmation."}
          </p>
        </div>
        {canEdit && (
          <button
            data-testid={`${testidPrefix}-new`}
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2"
          >
            <PlusIcon size={14} weight="bold" /> New Proforma
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input data-testid={`${testidPrefix}-search`} value={q} onChange={e => setQ(e.target.value)}
               placeholder="Search by PI no / buyer / source doc / cust PO…"
               className="flex-1 h-10 px-3 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-sm" />
        <select data-testid={`${testidPrefix}-status-filter`} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="h-10 px-3 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-sm">
          <option value="">All statuses</option>
          <option>Draft</option><option>Issued</option><option>Amended</option><option>Cancelled</option>
        </select>
      </div>

      <div className="tactical-card overflow-x-auto">
        {/* PI Register V1 · Jun 2026 — density toggle + column chooser, mirroring
            the sealed SO/ESO register family. Pinned: PI No + Actions. */}
        <div className="flex items-center justify-end px-4 py-2 border-b border-[var(--border)] relative gap-2">
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" role="group" aria-label="Row density">
            {[["comfortable", "Comfy"], ["compact", "Compact"]].map(([k, l]) => (
              <button key={k} data-testid={`${testidPrefix}-density-${k}`} onClick={() => setDensityPersisted(k)}
                className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 ${density === k ? "bg-[var(--accent)]/15 text-[var(--accent)]" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
            ))}
          </div>
          <button data-testid={`${testidPrefix}-cols-btn`} onClick={() => setColsMenu(v => !v)}
            className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] rounded-sm text-zinc-300 flex items-center gap-2"
            title="Show / hide columns">
            <span className="font-mono">{visibleCols.size}/{PI_ALL_COLS.length}</span> Columns
          </button>
          {colsMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setColsMenu(false)} />
              <div className="absolute right-4 top-11 z-40 w-64 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl p-2 max-h-96 overflow-y-auto" data-testid={`${testidPrefix}-cols-menu`}>
                <div className="flex items-center justify-between px-1 pb-2 border-b border-[var(--border)] mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Show columns</div>
                  <button onClick={resetCols} data-testid={`${testidPrefix}-cols-reset`} className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline">Reset</button>
                </div>
                {PI_ALL_COLS.map(c => (
                  <label key={c.k} className="flex items-center gap-2 px-2 py-1 rounded-sm text-xs cursor-pointer hover:bg-[var(--bg-2)]">
                    <input type="checkbox" data-testid={`${testidPrefix}-col-${c.k}`} checked={visibleCols.has(c.k)} onChange={() => toggleCol(c.k)} className="w-3.5 h-3.5 accent-[var(--accent)]" />
                    <span className="text-zinc-200">{c.label}</span>
                  </label>
                ))}
                <div className="px-3 py-1.5 border-t border-[var(--border)] text-[10px] text-zinc-500 mt-1">Preference saved per user (localStorage).</div>
              </div>
            </>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className={`text-left ${pad}`}>PI No</th>
              {col("date")        && <th className={`text-left ${pad}`}>PI Date</th>}
              {col("buyer")       && <th className={`text-left ${pad}`}>Customer</th>}
              {col("item")        && <th className={`text-right ${pad}`}>Item No</th>}
              {col("style")       && <th className={`text-left ${pad}`}>Style No</th>}
              {col("article")     && <th className={`text-left ${pad}`}>Product Code</th>}
              {col("qty")         && <th className={`text-right ${pad}`}>Quantity</th>}
              {col("rate")        && <th className={`text-right ${pad}`}>Rate</th>}
              {col("value")       && <th className={`text-right ${pad}`}>Value</th>}
              {col("status")      && <th className={`text-left ${pad}`}>Status</th>}
              {col("source")      && <th className={`text-left ${pad}`}>{isExport ? "Source ESO" : "Source SO"}</th>}
              {col("currency")    && <th className={`text-left ${pad}`}>Currency</th>}
              {col("buyer_po")    && <th className={`text-left ${pad}`}>Buyer PO No</th>}
              {col("hsn")         && <th className={`text-left ${pad}`}>HSN</th>}
              {col("description") && <th className={`text-left ${pad}`}>Description</th>}
              {col("discount")    && <th className={`text-right ${pad}`}>Disc %</th>}
              {col("gst")         && <th className={`text-right ${pad}`}>GST %</th>}
              {col("fx")          && <th className={`text-right ${pad}`}>FX Rate</th>}
              {col("converted_eso") && <th className={`text-left ${pad}`}>Converted ESO</th>}
              {col("grand_total") && <th className={`text-right ${pad}`}>Grand Total</th>}
              <th className={`text-right ${pad}`}>Actions</th>
            </tr>
          </thead>
          <tbody data-testid={`${testidPrefix}-rows`}>
            {loading ? <tr><td colSpan={totalColSpan} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={totalColSpan} className="px-3 py-8 text-center text-zinc-500">No Proforma invoices yet.</td></tr>
                : filtered.flatMap(r => {
                  const lines = r.lines || [];
                  const linesToRender = lines.length ? lines : [null];
                  const ccy = r.currency || "INR";
                  const isCancelled = r.status === "Cancelled";
                  const id8 = r.id.slice(0, 8);
                  return linesToRender.map((line, lineIdx) => {
                    const isFirst = lineIdx === 0;
                    const isLast = lineIdx === linesToRender.length - 1;
                    return (
                      <tr key={`${r.id}-${lineIdx}`}
                        data-testid={`${testidPrefix}-row-${id8}-${lineIdx}`}
                        className={`${!isLast ? "border-b border-[var(--border)]/40" : "border-b border-[var(--border)]"} align-top hover:bg-[var(--bg-2)]/30 ${isCancelled ? "[&>td]:text-zinc-500 opacity-75" : ""}`}>
                        {/* PI No — full number on one line, repeated per line-item row */}
                        <td className={`${pad} font-mono`}>
                          <span data-testid={isFirst ? `${testidPrefix}-row-doc-${id8}` : `${testidPrefix}-row-doc-${id8}-${lineIdx}`}
                            className="font-mono whitespace-nowrap text-[var(--accent)] text-xs font-semibold">{r.invoice_no}</span>
                        </td>
                        {col("date")  && <td className={`${pad} text-zinc-400 font-mono whitespace-nowrap text-xs`}>{r.invoice_date || "—"}</td>}
                        {col("buyer") && <td className={`${pad}`}><span className="text-zinc-200 font-medium leading-tight" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", maxWidth: "180px" }} title={r.buyer?.name || "—"}>{r.buyer?.name || "—"}</span></td>}
                        {col("item")  && <td className={`${pad} text-right font-mono text-zinc-300 text-xs`}>{line ? (line.item_no || "—") : "—"}</td>}
                        {col("style") && <td className={`${pad} font-mono text-zinc-300 text-xs`}>{line ? (line.style_no || "—") : "—"}</td>}
                        {col("article") && <td className={`${pad}`}>{line ? <span className="font-mono text-[var(--accent)] text-xs" title={line.article_no || ""}>{line.article_no || "—"}</span> : <span className="text-zinc-500">—</span>}</td>}
                        {col("qty")   && <td className={`${pad} text-right`}>{line ? (
                          <span className="font-mono font-semibold text-white tabular-nums text-xs" data-testid={`${testidPrefix}-line-qty-${id8}-${lineIdx}`}>
                            {Number(line.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}<span className="text-zinc-500 ml-1 text-[10px]">{line.unit || ""}</span>
                          </span>) : <span className="text-zinc-500">—</span>}</td>}
                        {col("rate")  && <td className={`${pad} text-right font-mono text-zinc-300 tabular-nums text-xs`}>{line ? fmtPiRate(line.rate, ccy) : "—"}</td>}
                        {col("value") && <td className={`${pad} text-right font-mono tabular-nums text-xs font-semibold ${ccy === "INR" ? "text-white" : "text-emerald-300"}`} data-testid={`${testidPrefix}-line-value-${id8}-${lineIdx}`}>{line ? fmtPiVal(line.amount, ccy) : "—"}</td>}
                        {col("status") && <td className={`${pad}`}>{(() => {
                          // Lifecycle PRIMARY · authorisation SECONDARY. Repeated per PI
                          // line (PI status is header-level). Business gates unchanged.
                          const life = isCancelled ? "Cancelled" : (r.status || "Draft");
                          const auth = piAuthLabel(r.approval_status);
                          return (
                            <div className="flex flex-col gap-1" data-testid={`${testidPrefix}-status-${id8}-${lineIdx}`}
                              title={r.approval_status && r.approval_status !== "Not Submitted" ? `status: ${r.status || "Draft"} · approval: ${r.approval_status}` : `status: ${r.status || "Draft"}`}>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest w-fit ${STATUS_STYLE[life] || STATUS_STYLE.Draft}`}>{life}</span>
                              <span className="text-[10px] font-mono text-zinc-500 w-fit">{auth}</span>
                            </div>
                          );
                        })()}</td>}
                        {col("source") && <td className={`${pad} text-zinc-400 font-mono text-xs whitespace-nowrap`}>{r.source?.no || "—"}</td>}
                        {col("currency") && <td className={`${pad} font-mono text-xs`}><span className="text-zinc-300">{ccy}</span>{isExport && r.fx_rate ? <span className="text-zinc-500 ml-1.5">@ {r.fx_rate}</span> : null}</td>}
                        {col("buyer_po") && <td className={`${pad} text-zinc-400 font-mono text-xs whitespace-nowrap`}>{r.references?.customer_po_no || "—"}</td>}
                        {col("hsn")   && <td className={`${pad} text-zinc-400 font-mono text-xs`}>{line ? (line.hsn_code || "—") : "—"}</td>}
                        {col("description") && <td className={`${pad} text-zinc-300 text-xs`}>{line ? <span title={line.description || ""}>{line.description || "—"}</span> : "—"}</td>}
                        {col("discount") && <td className={`${pad} text-right font-mono text-zinc-400 tabular-nums text-xs`}>{line ? `${Number(line.discount_pct || 0)}%` : "—"}</td>}
                        {col("gst")   && <td className={`${pad} text-right font-mono text-zinc-400 tabular-nums text-xs`}>{line ? `${Number(line.gst_rate ?? 0)}%` : "—"}</td>}
                        {col("fx")    && <td className={`${pad} text-right font-mono text-zinc-400 tabular-nums text-xs`}>{isFirst ? (r.fx_rate ? Number(r.fx_rate).toFixed(2) : "—") : null}</td>}
                        {col("converted_eso") && <td className={`${pad} text-xs`}>{isFirst ? (r.converted_to_eso_no ? <span title={`Converted to ${r.converted_to_eso_no}`} className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-purple-500/40 text-purple-300 bg-purple-500/5 text-[9px] font-mono">→ {r.converted_to_eso_no}</span> : <span className="text-zinc-600">—</span>) : null}</td>}
                        {col("grand_total") && <td className={`${pad} text-right font-mono tabular-nums text-xs text-zinc-300`} title="PI header-level Grand Total">{isFirst ? fmtPiVal(r.totals?.grand_total, ccy) : null}</td>}
                        {/* Actions — PI-level, rendered once per PI (first line row) */}
                        <td className={`${pad} text-right`}>
                          {isFirst ? (() => {
                            const items = [];
                            items.push(<DropdownMenuItem key="view" data-testid={`${testidPrefix}-view-${id8}`} onClick={() => openEdit(r)}>View</DropdownMenuItem>);
                            if (canEdit && r.status !== "Cancelled" && !isAuthorisedPi(r) && !isPendingDomesticPi(r) && !isPendingExportPi(r))
                              items.push(<DropdownMenuItem key="edit" data-testid={`${testidPrefix}-edit-${id8}`} onClick={() => openEdit(r)}>{r.status === "Draft" ? "Edit" : "Edit (amend reason on save)"}</DropdownMenuItem>);
                            items.push(<DropdownMenuItem key="print" className="text-sky-300" data-testid={`${testidPrefix}-print-html-${id8}`} onClick={() => printHtml(r)}>Browser Print (A4)</DropdownMenuItem>);
                            items.push(<DropdownMenuItem key="dl-pdf" className="text-rose-300" data-testid={`${testidPrefix}-download-pdf-${id8}`} onClick={() => downloadDoc(isExport ? "EPI" : "PI", r.id, "pdf")}>Download PDF</DropdownMenuItem>);
                            items.push(<DropdownMenuItem key="dl-xlsx" className="text-emerald-300" data-testid={`${testidPrefix}-export-excel-${id8}`} onClick={() => downloadDoc(isExport ? "EPI" : "PI", r.id, "xlsx")}>Export to Excel</DropdownMenuItem>);
                            if (canEdit && isExport && ["Issued", "Amended"].includes(r.status) && !r.converted_to_eso_id)
                              items.push(<DropdownMenuItem key="convert" className="text-purple-300" data-testid={`${testidPrefix}-convert-eso-${id8}`} onClick={() => convertToEso(r)}>Convert to Export SO…</DropdownMenuItem>);
                            if (canEdit && r.status === "Draft" && !isAuthorisedPi(r) && !isPendingDomesticPi(r) && !isPendingExportPi(r))
                              items.push(<DropdownMenuItem key="del-draft" className="text-red-300" data-testid={`${testidPrefix}-delete-${id8}`} onClick={() => remove(r)}>Delete Draft</DropdownMenuItem>);
                            if (canEdit && r.status !== "Draft" && r.status !== "Cancelled")
                              items.push(<DropdownMenuItem key="cancel" className="text-amber-300" data-testid={`${testidPrefix}-cancel-${id8}`} onClick={() => cancel(r)}>Cancel…</DropdownMenuItem>);
                            if (hasPerm?.("proforma_invoices:delete") && r.status !== "Draft" && !isAuthorisedPi(r) && !isPendingDomesticPi(r) && !isPendingExportPi(r))
                              items.push(<DropdownMenuItem key="hard-del" className="text-red-400" data-testid={`${testidPrefix}-hard-delete-${id8}`} onClick={() => remove(r)}>Delete</DropdownMenuItem>);
                            return (
                              <div className="flex justify-end">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button data-testid={`${testidPrefix}-actions-${id8}`} className="h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap">Actions ▾</button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm min-w-[200px]">
                                    {items}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            );
                          })() : null}
                        </td>
                      </tr>
                    );
                  });
                })}
          </tbody>
        </table>
      </div>

      {editorOpen && (
        <PiEditor
          piType={piType}
          initial={editing}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={async () => { setEditorOpen(false); setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function PiEditor({ piType, initial, onClose, onSaved }) {
  const isExport = piType === "export";
  const isNew = !initial;
  const savedStyles = useDocStyles(initial?.id ? (isExport ? "EPI" : "PI") : null, initial?.id);
  // Feb 26, 2026 · Only Cancelled PIs are truly read-only. Issued/Amended
  // remain editable — Save routes through the Amend flow (reason required).
  // DP-PI-AUTH-EDIT-LOCK · Phase 2 · Feb 2026 — Authorised PIs are also
  // fully read-only for direct Edit. Backend enforces this authoritatively;
  // this gate merely prevents the editor from entering commercial edit mode.
  // DP-PI-PENDING-FREEZE · Phase 2 · Feb 2026 — Domestic PIs whose
  // approval_status is "Pending Approval" are also locked for direct
  // Edit while Management is deciding. EPI (export PIs) handled by
  // the parallel `isPendingExportPi` predicate.
  // DP-EPI-PENDING-FREEZE · Phase 2 · Feb 2026 — Export PIs whose
  // approval_status is "Pending Approval" are additionally locked
  // for direct Edit. Sealed Domestic behaviour preserved.
  const readOnly = initial && (initial.status === "Cancelled" || isAuthorisedPi(initial) || isPendingDomesticPi(initial) || isPendingExportPi(initial));
  const isAmendable = initial && ["Issued", "Amended"].includes(initial.status);
  // Sep 03, 2026 · Bug fix — when opening an existing PI, derive the
  // Source dropdown value from the persisted `source.type` so a PI
  // created via "Direct Buyer (no SO)" doesn't re-open as "Sales Order".
  // Backend stamps `source.type` as "SO" / "ESO" / "CUST" (see
  // routes/proforma_invoices.py::prefill).
  const [srcType, setSrcType] = useState(() => {
    const t = (initial?.source?.type || "").toUpperCase();
    if (t === "CUST" || t === "SO" || t === "ESO") return t;
    return isExport ? "ESO" : "SO";
  });
  const [srcOptions, setSrcOptions] = useState([]);
  const [srcId, setSrcId] = useState(initial?.source?.id || "");
  const [busy, setBusy] = useState(false);
  // Feb 2026 · Preload masters so the editor can offer a Customer picker
  // (for the "Direct Buyer" flow) and an Article-Master datalist per line.
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tncTemplates, setTncTemplates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [cRes, aRes, tRes] = await Promise.all([
          api.get("/customers/"),
          api.get("/articles/?limit=2000").catch(() => api.get("/articles/")),
          api.get("/tnc-templates/?template_type=sales"),
        ]);
        setCustomers(cRes.data || []);
        setArticles((aRes.data || []).filter(a => a.is_active !== false).map(a => ({
          id: a.id, article_no: a.product_code || a.code || "",
          description: a.description || "",
          hsn_code: a.hs_code || a.hsn_code || "",
          rate: a.rate || 0, unit: a.unit || "MTR",
          // Jun 2026 · authoritative Product-Master Style No. carried through
          // for line-editor auto-pickup (frontend-only; same API payload).
          style_no: a.style_no || "",
        })));
        const tpls = tRes.data || [];
        setTncTemplates(tpls);
        // Feb 2026 · Auto-apply the default template on new drafts so the
        // editor visually matches what the backend would stamp on save.
        if (isNew) {
          const def = tpls.find(t => t.is_default);
          if (def) {
            setPi(prev => (prev.terms_and_conditions || prev.tnc_template_id)
              ? prev
              : { ...prev, terms_and_conditions: def.text, tnc_template_id: def.id });
          }
        }
      } catch {}
    })();
  }, []);
  const [pi, setPi] = useState(initial || {
    pi_type: piType,
    invoice_date: new Date().toISOString().slice(0, 10),
    source: {}, buyer: {}, seller: {}, lines: [],
    currency: isExport ? "USD" : "INR",
    fx_rate: 0,
    incoterms: isExport ? "FOB" : "",
    port_of_loading: "", port_of_discharge: "", country_of_final_destination: "",
    pre_carriage_by: "", vessel_flight_no: "",
    country_of_origin: isExport ? "India" : "",
    // Feb 27, 2026 · Buyer PO + Delivery + Shipping Marks metadata.
    references: { so_no: "", eso_no: "", customer_po_no: "", customer_po_date: "" },
    delivery_location: "", delivery_address: "",
    shipping_marks: { port_of_destination: "", country_of_final_destination: "",
                      carton_from: "", carton_to: "", made_in: "MADE IN INDIA" },
    advance_pct: 0, validity_days: 30, delivery_terms: "",
    references: {}, notes: "",
    terms: isExport
      ? "Advance / balance against BL copy. Validity: 30 days. Prices are indicative."
      : "Advance / balance on dispatch. Validity: 30 days. Not a tax invoice.",
    charges: { freight: 0, insurance: 0, packing: 0, tax_pct: 0 },
    totals: {}, status: "Draft",
  });

  useEffect(() => {
    (async () => {
      try {
        let opts = [];
        if (srcType === "CUST") {
          opts = customers.map(c => ({ id: c.id, no: c.code, sub: c.name }));
        } else {
          const path = isExport ? "/exports/orders/" : "/sales-orders/";
          const { data } = await api.get(path);
          opts = (data || []).map(x => ({
            id: x.id,
            no: x.doc_no || x.order_no,
            sub: x.customer_name || x.buyer_name || "",
          }));
        }
        setSrcOptions(opts);
      } catch { setSrcOptions([]); }
    })();
  }, [isExport, srcType, customers]);

  const prefill = async () => {
    if (!srcId) { toast.error(`Pick a source ${srcType === "CUST" ? "customer" : (isExport ? "ESO" : "SO")} first`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/proforma-invoices/prefill", {
        pi_type: piType, src_id: srcId,
        src_type: srcType === "CUST" ? "CUST" : undefined,
      });
      setPi(prev => ({ ...prev, ...data, status: prev.status || "Draft",
                        charges: prev.charges, notes: prev.notes, terms: prev.terms,
                        advance_pct: prev.advance_pct, validity_days: prev.validity_days }));
      toast.success(srcType === "CUST"
        ? `Buyer set to ${data.buyer.name} — add line items manually below`
        : `Prefilled ${data.lines.length} line(s) from ${data.source.no}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Prefill failed"); }
    finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const ln of (pi.lines || [])) {
      const q = Number(ln.qty) || 0, r = Number(ln.rate) || 0;
      subtotal += q * r * (1 - (Number(ln.discount_pct) || 0) / 100);
    }
    const c = pi.charges || {};
    const freight = Number(c.freight) || 0, ins = Number(c.insurance) || 0, pk = Number(c.packing) || 0;
    const headerTax = Number(c.tax_pct) || 0;
    // Mar 2026 · Per-line GST parity with GST Tax Invoice. Each line
    // apportions freight/ins/packing pro-rata by amount, then applies its
    // own `gst_rate` (falls back to header `tax_pct` for legacy rows).
    const isExport = (pi.buyer?.country || "India").toLowerCase() !== "india";
    const ssc = (pi.seller?.state_code || "").trim();
    const bsc = (pi.buyer?.state_code || "").trim();
    const taxType = isExport ? "export" : (ssc && bsc && ssc === bsc ? "intra" : "inter");
    let totalCgst = 0, totalSgst = 0, totalIgst = 0, taxAmt = 0;
    if (!isExport && (pi.currency || "INR") === "INR") {
      for (const ln of (pi.lines || [])) {
        const q = Number(ln.qty) || 0, r = Number(ln.rate) || 0;
        const lineAmt = q * r * (1 - (Number(ln.discount_pct) || 0) / 100);
        const share = subtotal ? lineAmt / subtotal : 0;
        const base = lineAmt + share * (freight + ins + pk);
        const rate = Number(ln.gst_rate ?? headerTax) || 0;
        const t = base * rate / 100;
        if (taxType === "intra") { totalCgst += t / 2; totalSgst += t / 2; }
        else                     { totalIgst += t; }
        taxAmt += t;
      }
    }
    const grandExact = subtotal + freight + ins + pk + taxAmt;
    const grandRounded = Math.round(grandExact);
    const roundOff = Number((grandRounded - grandExact).toFixed(2));
    return { subtotal, freight, insurance: ins, packing: pk,
             tax_type: taxType, tax_pct: headerTax, tax_amt: taxAmt,
             total_cgst: totalCgst, total_sgst: totalSgst, total_igst: totalIgst,
             round_off: roundOff, grand_total: grandRounded };
  }, [pi.lines, pi.charges, pi.buyer, pi.seller, pi.currency]);

  const patchLine = (idx, patch) => setPi(prev => {
    const lines = [...(prev.lines || [])]; lines[idx] = { ...lines[idx], ...patch };
    return { ...prev, lines };
  });
  const addLine = () => setPi(prev => ({ ...prev, lines: [...(prev.lines || []),
    { line_no: (prev.lines?.length || 0) + 1, article_no: "", style_no: "", description: "", hsn_code: "",
      unit: "MTR", qty: 0, rate: 0, discount_pct: 0, gst_rate: Number(prev.charges?.tax_pct) || 5 }] }));
  const removeLine = (idx) => setPi(prev => ({ ...prev,
    lines: (prev.lines || []).filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_no: i + 1 })) }));

  // Product-Master pick/autofill shared by both line-editor layouts. Style No.
  // auto-pickup fires here (active user selection) only — never on render, so a
  // source-prefilled style_no is preserved. line.description still autofilled +
  // kept in state/payload even though the Domestic editor no longer shows it.
  const pickArticle = (idx, v, ln) => {
    const hit = articles.find(a => a.article_no === v);
    if (hit) {
      patchLine(idx, {
        article_no: hit.article_no,
        style_no: hit.style_no || "",
        description: hit.description || ln.description,
        hsn_code: hit.hsn_code || ln.hsn_code,
        unit: hit.unit || ln.unit,
        rate: hit.rate || ln.rate,
        gst_rate: (ln.gst_rate == null || ln.gst_rate === "")
          ? (hit.gst_rate ?? Number(pi.charges?.tax_pct) ?? 5)
          : ln.gst_rate,
      });
    } else {
      patchLine(idx, { article_no: v });
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) { const { data } = await api.post("/proforma-invoices/", pi); toast.success("Draft saved"); setPi(data); }
      else       { const { data } = await api.put(`/proforma-invoices/${initial.id}`, pi); toast.success("Draft updated"); setPi(data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const issue = async () => {
    setBusy(true);
    try {
      let id = initial?.id;
      if (isNew) { const { data } = await api.post("/proforma-invoices/", pi); id = data.id; setPi(data); }
      else if (initial.status === "Draft") { const { data } = await api.put(`/proforma-invoices/${id}`, pi); setPi(data); }
      const { data: issued } = await api.post(`/proforma-invoices/${id}/issue`);
      toast.success(`${issued.invoice_no} · issued`);
      onSaved && await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setBusy(false); }
  };

  const amend = async () => {
    const reason = window.prompt(`Amend ${initial.invoice_no}?\nReason (min 4 chars):`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason ≥ 4 chars"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/proforma-invoices/${initial.id}/amend`, {
        reason: reason.trim(),
        updates: { lines: pi.lines, charges: pi.charges, buyer: pi.buyer, notes: pi.notes,
                   terms: pi.terms, invoice_date: pi.invoice_date,
                   advance_pct: pi.advance_pct, validity_days: pi.validity_days,
                   delivery_terms: pi.delivery_terms,
                   incoterms: pi.incoterms, port_of_loading: pi.port_of_loading,
                   port_of_discharge: pi.port_of_discharge,
                   country_of_final_destination: pi.country_of_final_destination,
                   pre_carriage_by: pi.pre_carriage_by,
                   vessel_flight_no: pi.vessel_flight_no,
                   country_of_origin: pi.country_of_origin,
                   references: pi.references,
                   delivery_location: pi.delivery_location,
                   delivery_address: pi.delivery_address,
                   shipping_marks: pi.shipping_marks },
      });
      toast.success(`${data.invoice_no} · amended`);
      onSaved && await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Amend failed"); }
    finally { setBusy(false); }
  };

  const submitForApproval = async () => {
    setBusy(true);
    let draftId = initial?.id;
    let draftPersisted = !isNew;
    try {
      if (isNew) {
        const { data } = await api.post("/proforma-invoices/", pi);
        draftId = data.id;
        draftPersisted = true;
        setPi(data);
      } else if (initial.status === "Draft") {
        const { data } = await api.put(`/proforma-invoices/${draftId}`, pi);
        setPi(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save as Draft failed");
      setBusy(false);
      return;
    }
    try {
      await api.post(`/proforma-invoices/${draftId}/submit-for-approval`);
      toast.success("Submitted for authorisation");
      onSaved && await onSaved();
    } catch (e) {
      // Phase 1B.1 · Failure safety — retain the saved Draft, no duplicate,
      // never falsely display Pending Authorisation.
      if (draftPersisted) {
        toast.error("Document was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
        onSaved && await onSaved({ silent: true });
      } else {
        toast.error(e?.response?.data?.detail || "Submit for Authorisation failed");
      }
    } finally {
      setBusy(false);
    }
  };

  // Sep 03, 2026 · Approve / Reject buttons in the footer removed — the
  // shared <ApprovalControls> widget above the form is now the single
  // source of truth for authorise / reject / un-authorise actions
  // (matches the SO / ESO / PO / WO pattern). Only Submit-for-
  // Authorisation stays in the footer since that endpoint is PI-specific
  // and not part of the shared widget.

  const tid = isExport ? "epi" : "pi";
  const isAuthorised = ["Approved", "Authorized", "Authorised"].includes(pi.approval_status);
  const canIssue = !readOnly && (pi.lines?.length || 0) > 0 && !!pi.buyer?.name && isAuthorised;
  // Phase 1B.1 · PI/EPI New-footer visibility ≠ enabled-state.
  // showSubmit  = lifecycle permits a Submit (Not-Submitted / Draft / Rejected,
  //               never Pending / Authorised / Issued / Amended / Cancelled).
  // enableSubmit = showSubmit AND mandatory submission requirements satisfied.
  // Button is RENDERED whenever showSubmit, DISABLED when !enableSubmit so the
  // operator can see the correct next-step action from an empty New record.
  const showSubmit = !readOnly && canSubmitDoc(isExport ? "EPI" : "PI")
    && !isAmendable
    && (!pi.approval_status || pi.approval_status === "Not Submitted" || pi.approval_status === "Rejected");
  const submitReqsMet = (pi.lines?.length || 0) > 0 && !!pi.buyer?.name;
  const enableSubmit = showSubmit && submitReqsMet;
  const submitDisabledReason = !submitReqsMet
    ? "Add buyer and at least one line to submit for authorisation"
    : "";
  const isPending = pi.approval_status === "Pending Approval";
  const isAdmin = (typeof window !== "undefined" && window.__user_role) === "System Administrator";

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent
        data-testid={`${tid}-editor-dialog`}
        className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[1200px] max-h-[92vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()} description="Proforma Invoice action — draft, edit or convert to a Tax Invoice."
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <FileTextIcon size={18} weight="bold" className="text-[var(--accent)]" />
            {isNew ? "New Proforma Invoice" : `Proforma ${pi.invoice_no}`}
            {isExport && <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/5 px-2 py-0.5 rounded-sm">Export</span>}
            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[pi.status] || ""}`}>{pi.status}</span>
          </DialogTitle>
        </DialogHeader>
        {/* Phase 1B.1 · Scoped top-right Close chip — enlarges the hit target
            around the shared 16x16 shadcn X and gives the operator an obvious
            exit without having to scroll to the footer. Reuses the same
            onClose that the footer Close + the X button both call. */}
        <button
          type="button"
          data-testid={`${tid}-close-top`}
          onClick={() => !busy && onClose()}
          aria-label="Close dialog"
          className="absolute right-10 top-3 z-10 h-8 px-2.5 inline-flex items-center gap-1.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] font-mono uppercase tracking-widest hover:bg-white/10 disabled:opacity-40"
          disabled={busy}
        >
          <XIcon size={12} weight="bold" /> Close
        </button>

        {/* Mar 1, 2026 · Per-doc Approval Workflow (Admin/Manager only) */}
        {pi?.id && (
          <ApprovalControls
            docType={isExport ? "epi" : "pi"}
            docId={pi.id}
            docNo={pi.invoice_no || "(new)"}
            approvalStatus={pi.approval_status}
            rejectionReason={pi.rejection_reason || pi.rejected_reason}
            approvalHistory={pi.approval_history}
            onChange={async () => {
              try {
                const { data } = await api.get(`/proforma-invoices/${pi.id}`);
                setPi(data);
                onSaved && await onSaved({ silent: true });
              } catch {}
            }}
            testIdPrefix={`${tid}-approval`}
          />
        )}

        {pi?.id && (
          <DocumentInfo docNo={pi.invoice_no} createdAt={pi.created_at} updatedAt={pi.updated_at} createdBy={pi.created_by} docLabel="Invoice No." testidPrefix="pi" />
        )}

        {!readOnly && (
          <div className="grid grid-cols-12 gap-3 border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
            <label className="col-span-2 flex items-center text-[11px] uppercase tracking-widest text-zinc-400">
              Source
            </label>
            <select data-testid={`${tid}-src-type`} value={srcType}
                    onChange={e => { setSrcType(e.target.value); setSrcId(""); }}
                    className="col-span-2 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
              <option value={isExport ? "ESO" : "SO"}>{isExport ? "Export SO" : "Sales Order"}</option>
              <option value="CUST">Direct Buyer (no {isExport ? "ESO" : "SO"})</option>
            </select>
            <select data-testid={`${tid}-src-id`} value={srcId} onChange={e => setSrcId(e.target.value)}
                    className="col-span-6 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
              <option value="">— pick a {srcType === "CUST" ? "customer" : (isExport ? "ESO" : "SO")} —</option>
              {srcOptions.map(o => <option key={o.id} value={o.id}>{o.no} {o.sub ? `· ${o.sub}` : ""}</option>)}
            </select>
            <button data-testid={`${tid}-prefill-btn`} disabled={busy || !srcId} onClick={prefill}
                    className="col-span-2 h-9 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
              Prefill
            </button>
          </div>
        )}

        <div className="grid grid-cols-4 gap-3">
          <Field label="PI Date"><input type="date" disabled={readOnly} value={pi.invoice_date || ""} onChange={e => setPi(prev => ({ ...prev, invoice_date: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
          <Field label="Currency"><input disabled={readOnly} value={pi.currency || ""} onChange={e => setPi(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
          {isExport && <Field label="FX Rate"><input type="number" step="0.01" disabled={readOnly} value={pi.fx_rate || 0} onChange={e => setPi(prev => ({ ...prev, fx_rate: Number(e.target.value) || 0 }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-right tabular-nums" /></Field>}
          <Field label="Advance %"><input type="number" min="0" max="100" step="0.5" disabled={readOnly} value={pi.advance_pct || 0} onChange={e => setPi(prev => ({ ...prev, advance_pct: Number(e.target.value) || 0 }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-right tabular-nums" /></Field>
          {!isExport && <Field label="Validity (days)"><input type="number" min="0" disabled={readOnly} value={pi.validity_days || 0} onChange={e => setPi(prev => ({ ...prev, validity_days: Number(e.target.value) || 0 }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-right tabular-nums" /></Field>}
        </div>

        {isExport && (
          <div className="border border-amber-500/30 rounded-sm p-3 bg-amber-500/5 space-y-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300">Export Logistics · Customs / Shipping</div>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Incoterms"><input disabled={readOnly} data-testid={`${tid}-incoterms`} value={pi.incoterms || ""} onChange={e => setPi(prev => ({ ...prev, incoterms: e.target.value.toUpperCase() }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
              <Field label="Pre-Carriage By"><input disabled={readOnly} data-testid={`${tid}-pre-carriage`} value={pi.pre_carriage_by || ""} onChange={e => setPi(prev => ({ ...prev, pre_carriage_by: e.target.value }))} placeholder="e.g. Road, Rail" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
              <Field label="Vessel / Flight No"><input disabled={readOnly} data-testid={`${tid}-vessel-flight`} value={pi.vessel_flight_no || ""} onChange={e => setPi(prev => ({ ...prev, vessel_flight_no: e.target.value }))} placeholder="e.g. MSC BRUNELLA / EK 5008" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
              <Field label="Country of Origin"><input disabled={readOnly} data-testid={`${tid}-country-origin`} value={pi.country_of_origin || "India"} onChange={e => setPi(prev => ({ ...prev, country_of_origin: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Port of Loading"><input disabled={readOnly} data-testid={`${tid}-pol`} value={pi.port_of_loading || ""} onChange={e => setPi(prev => ({ ...prev, port_of_loading: e.target.value }))} placeholder="e.g. Nhava Sheva, INDIA" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
              <Field label="Port of Discharge"><input disabled={readOnly} data-testid={`${tid}-pod`} value={pi.port_of_discharge || ""} onChange={e => setPi(prev => ({ ...prev, port_of_discharge: e.target.value }))} placeholder="e.g. Hamburg, DE" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
              <Field label="Final Destination"><input disabled={readOnly} data-testid={`${tid}-destination`} value={pi.country_of_final_destination || ""} onChange={e => setPi(prev => ({ ...prev, country_of_final_destination: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
            </div>
          </div>
        )}

        {/* Feb 27, 2026 · Buyer PO + Delivery + Shipping Marks block.
            Buyer PO is available for both PI types (domestic + export); the
            Delivery / Shipping Marks fields are export-only per user spec. */}
        <div className="border border-sky-500/30 rounded-sm p-3 bg-sky-500/5 space-y-3">
          <div className="text-[11px] font-mono uppercase tracking-widest text-sky-300">Order Reference · Delivery{isExport ? " · Shipping Marks" : ""}</div>
          <div className="grid grid-cols-4 gap-3">
            <Field label="Buyer PO No"><input disabled={readOnly} data-testid={`${tid}-buyer-po-no`} value={pi.references?.customer_po_no || ""} onChange={e => setPi(prev => ({ ...prev, references: { ...(prev.references || {}), customer_po_no: e.target.value } }))} placeholder="e.g. PO/2026/00123" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
            <Field label="Buyer PO Date"><input type="date" disabled={readOnly} data-testid={`${tid}-buyer-po-date`} value={pi.references?.customer_po_date || ""} onChange={e => setPi(prev => ({ ...prev, references: { ...(prev.references || {}), customer_po_date: e.target.value } }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
            <Field label="Delivery Location"><input disabled={readOnly} data-testid={`${tid}-delivery-location`} value={pi.delivery_location || ""} onChange={e => setPi(prev => ({ ...prev, delivery_location: e.target.value }))} placeholder="e.g. Buyer Warehouse · Hamburg" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
            <Field label="Delivery Address"><textarea disabled={readOnly} data-testid={`${tid}-delivery-address`} rows="1" value={pi.delivery_address || ""} onChange={e => setPi(prev => ({ ...prev, delivery_address: e.target.value }))} placeholder="Multi-line address of delivery point" className="w-full min-h-[36px] px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm resize-y" /></Field>
          </div>
          {/* Feb 27, 2026 · Payment Terms field — persisted on buyer.payment_terms
              so the existing print HTML (logistics grid + convert-to-ESO) picks
              it up without a schema change. Visible on both PI types. */}
          <div className="grid grid-cols-4 gap-3">
            <Field label="Payment Terms" span={isExport ? 2 : 4}>
              <input disabled={readOnly} data-testid={`${tid}-payment-terms`}
                value={pi.buyer?.payment_terms || ""}
                onChange={e => setPi(prev => ({ ...prev, buyer: { ...(prev.buyer || {}), payment_terms: e.target.value } }))}
                placeholder={isExport ? "e.g. 30% Advance · 70% against BL" : "e.g. Net 30 · 100% Advance"}
                className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" />
            </Field>
            {isExport && <Field label="Credit Days">
              <input disabled={readOnly} data-testid={`${tid}-credit-days`} type="number"
                value={pi.buyer?.credit_days ?? ""}
                onChange={e => setPi(prev => ({ ...prev, buyer: { ...(prev.buyer || {}), credit_days: e.target.value === "" ? "" : Number(e.target.value) } }))}
                placeholder="0" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
            </Field>}
          </div>
          {isExport && (
            <div className="grid grid-cols-5 gap-3">
              <Field label="Port of Destination"><input disabled={readOnly} data-testid={`${tid}-mark-port-dest`} value={pi.shipping_marks?.port_of_destination || ""} onChange={e => setPi(prev => ({ ...prev, shipping_marks: { ...(prev.shipping_marks || {}), port_of_destination: e.target.value } }))} placeholder="e.g. Hamburg" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
              <Field label="Country of Final Destination"><input disabled={readOnly} data-testid={`${tid}-mark-country`} value={pi.shipping_marks?.country_of_final_destination || ""} onChange={e => setPi(prev => ({ ...prev, shipping_marks: { ...(prev.shipping_marks || {}), country_of_final_destination: e.target.value } }))} placeholder="e.g. Germany" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
              <Field label="Carton No · From"><input disabled={readOnly} data-testid={`${tid}-mark-carton-from`} value={pi.shipping_marks?.carton_from || ""} onChange={e => setPi(prev => ({ ...prev, shipping_marks: { ...(prev.shipping_marks || {}), carton_from: e.target.value } }))} placeholder="e.g. 1/10" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
              <Field label="Carton No · To"><input disabled={readOnly} data-testid={`${tid}-mark-carton-to`} value={pi.shipping_marks?.carton_to || ""} onChange={e => setPi(prev => ({ ...prev, shipping_marks: { ...(prev.shipping_marks || {}), carton_to: e.target.value } }))} placeholder="e.g. 10/10" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
              <Field label="Made In"><input disabled={readOnly} data-testid={`${tid}-mark-made-in`} value={pi.shipping_marks?.made_in || "MADE IN INDIA"} onChange={e => setPi(prev => ({ ...prev, shipping_marks: { ...(prev.shipping_marks || {}), made_in: e.target.value.toUpperCase() } }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
            </div>
          )}
        </div>

        {/* Phase UI-EPI/ECI.1 · Shipping / Packing Details — declared
            commercial values. Blank allowed; Gross must be >= Net when
            both supplied. Same section on the ECI create dialog. */}
        <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 space-y-3">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Shipping / Packing Details</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Number of Cartons"><input type="number" min="0" step="1" disabled={readOnly} data-testid={`${tid}-cartons`} value={pi.number_of_cartons ?? ""} onChange={e => setPi(prev => ({ ...prev, number_of_cartons: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0) }))} placeholder="e.g. 10" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono text-right tabular-nums" /></Field>
            <Field label="Net Weight (kg)"><input type="number" min="0" step="0.001" disabled={readOnly} data-testid={`${tid}-net-weight`} value={pi.net_weight ?? ""} onChange={e => setPi(prev => ({ ...prev, net_weight: e.target.value === "" ? "" : Math.max(0, parseFloat(e.target.value) || 0) }))} placeholder="0.000" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono text-right tabular-nums" /></Field>
            <Field label="Gross Weight (kg)"><input type="number" min="0" step="0.001" disabled={readOnly} data-testid={`${tid}-gross-weight`} value={pi.gross_weight ?? ""} onChange={e => setPi(prev => ({ ...prev, gross_weight: e.target.value === "" ? "" : Math.max(0, parseFloat(e.target.value) || 0) }))} placeholder="0.000" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono text-right tabular-nums" /></Field>
          </div>
          {Number(pi.gross_weight || 0) > 0 && Number(pi.net_weight || 0) > 0 && Number(pi.gross_weight || 0) < Number(pi.net_weight || 0) && (
            <div data-testid={`${tid}-weight-error`} className="text-[11px] text-red-400 font-mono">Gross Weight cannot be less than Net Weight.</div>
          )}
        </div>

        {/* Feb 2026 · When the PI is tied to a Customer Master record,
            expose their saved Bill / Ship addresses as pickers above the
            editable Bill To / Ship To blocks — mirrors the Sales-Order
            workflow. Picking an address fully repopulates the buyer
            fields (name / address / city / state / pincode / country /
            GSTIN / state_code / contact / phone) so the printed PI
            always reflects live master data. The blocks below remain
            editable for one-time overrides. */}
        {(() => {
          const cust = customers.find(c => c.id === pi.buyer?.customer_id);
          const addrs = Array.isArray(cust?.addresses) ? cust.addresses : [];
          if (readOnly || !cust || addrs.length === 0) return null;
          const optLabel = (a) => {
            const lbl = a.label === "Other" ? (a.custom_label || "Other") : a.label;
            const loc = [a.city, a.state].filter(Boolean).join(", ");
            return `${lbl}${loc ? " · " + loc : ""}`;
          };
          const applyBill = (a) => setPi(prev => ({ ...prev, buyer: {
            ...(prev.buyer || {}),
            name: cust.name || prev.buyer?.name,
            bill_to_address_id: a.id,
            billing_address: a.address || "",
            city: a.city || "",
            state: a.state || "",
            state_code: a.state_code || (a.gstin || "").slice(0, 2) || "",
            pincode: a.pincode || "",
            country: a.country || cust.country || "India",
            gstin: a.gstin || "",
            contact_person: a.contact_person || "",
            phone: a.phone || "",
            email: a.email || "",
          }}));
          const applyShip = (a) => setPi(prev => ({ ...prev, buyer: {
            ...(prev.buyer || {}),
            ship_to_address_id: a.id,
            ship_to_name: cust.name || prev.buyer?.ship_to_name || prev.buyer?.name,
            shipping_address: a.address || "",
            ship_city: a.city || "",
            ship_state: a.state || "",
            ship_state_code: a.state_code || (a.gstin || "").slice(0, 2) || "",
            ship_pincode: a.pincode || "",
            ship_country: a.country || cust.country || "India",
            ship_gstin: a.gstin || "",
            ship_contact_person: a.contact_person || "",
            ship_phone: a.phone || "",
          }}));
          const currBill = pi.buyer?.bill_to_address_id || (addrs.find(a => a.is_primary_bill) || addrs[0])?.id || "";
          const currShip = pi.buyer?.ship_to_address_id || (addrs.find(a => a.is_primary_ship) || addrs[0])?.id || "";
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={`Bill To Address · ${cust.name}`}>
                <select
                  data-testid={`${tid}-bill-address-pick`}
                  value={currBill}
                  onChange={e => { const a = addrs.find(x => x.id === e.target.value); if (a) applyBill(a); }}
                  className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm"
                >
                  {addrs.map(a => <option key={a.id} value={a.id}>{optLabel(a)}{a.is_primary_bill ? " ★" : ""}</option>)}
                </select>
              </Field>
              <Field label={`Ship To Address · ${cust.name}`}>
                <select
                  data-testid={`${tid}-ship-address-pick`}
                  value={currShip}
                  onChange={e => { const a = addrs.find(x => x.id === e.target.value); if (a) applyShip(a); }}
                  className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm"
                >
                  {addrs.map(a => <option key={a.id} value={a.id}>{optLabel(a)}{a.is_primary_ship ? " ★" : ""}</option>)}
                </select>
              </Field>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Party title="Seller" party={pi.seller} />
          <PartyEditable
            title="Bill To"
            party={{
              name: pi.buyer?.name || "",
              address: pi.buyer?.billing_address || "",
              city: pi.buyer?.city || "",
              state: pi.buyer?.state || "",
              state_code: pi.buyer?.state_code || "",
              pincode: pi.buyer?.pincode || "",
              country: pi.buyer?.country || "",
              gstin: pi.buyer?.gstin || "",
              payment_terms: pi.buyer?.payment_terms,
              credit_days: pi.buyer?.credit_days,
            }}
            readOnly={readOnly}
            testId={`${tid}-bill-to`}
            onChange={(patch) => setPi(prev => ({ ...prev, buyer: {
              ...(prev.buyer || {}),
              name: patch.name, billing_address: patch.address,
              city: patch.city, state: patch.state, state_code: patch.state_code,
              pincode: patch.pincode,
              country: patch.country, gstin: patch.gstin,
            }}))}
            showPayment
          />
          <PartyEditable
            title="Ship To"
            party={{
              name: pi.buyer?.ship_to_name || pi.buyer?.name || "",
              address: pi.buyer?.shipping_address || "",
              city: pi.buyer?.ship_city || pi.buyer?.city || "",
              state: pi.buyer?.ship_state || pi.buyer?.state || "",
              state_code: pi.buyer?.ship_state_code || pi.buyer?.state_code || "",
              pincode: pi.buyer?.ship_pincode || pi.buyer?.pincode || "",
              country: pi.buyer?.ship_country || pi.buyer?.country || "",
              gstin: pi.buyer?.ship_gstin || "",
            }}
            readOnly={readOnly}
            testId={`${tid}-ship-to`}
            hint={pi.buyer && !(pi.buyer.shipping_address) ? "Same as Bill To (edit to override)" : ""}
            onCopyFromBill={() => setPi(prev => ({
              ...prev, buyer: {
                ...(prev.buyer || {}),
                ship_to_address_id: prev.buyer?.bill_to_address_id || "",
                ship_to_name: prev.buyer?.name || "",
                shipping_address: prev.buyer?.billing_address || "",
                ship_city: prev.buyer?.city || "",
                ship_state: prev.buyer?.state || "",
                ship_state_code: prev.buyer?.state_code || "",
                ship_pincode: prev.buyer?.pincode || "",
                ship_country: prev.buyer?.country || "",
                ship_gstin: prev.buyer?.gstin || "",
              },
            }))}
            onChange={(patch) => setPi(prev => ({ ...prev, buyer: {
              ...(prev.buyer || {}),
              ship_to_name: patch.name, shipping_address: patch.address,
              ship_city: patch.city, ship_state: patch.state,
              ship_state_code: patch.state_code, ship_pincode: patch.pincode,
              ship_country: patch.country, ship_gstin: patch.gstin,
            }}))}
          />
        </div>

        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-2)]/40">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Line Items</div>
            {!readOnly && <button data-testid={`${tid}-add-line`} onClick={addLine} className="text-xs px-2 py-1 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 rounded-sm flex items-center gap-1"><PlusIcon size={11} /> Add Line</button>}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                {/* Phase 1D (EPI replication) · Jun 2026 — Domestic & Export PI
                    now share the proven SO-style horizontal line grid:
                    Item No → Style No → Product Code → HSN → Qty → Unit → Rate →
                    Disc% → [GST% domestic-only] → Amount. GST% stays a separate
                    !isExport cell so Export keeps its genuine tax treatment. */}
                <th className="px-2 py-2 text-left w-16">Item No</th>
                <th className="px-2 py-2 text-left">Product Code</th>
                <th className="px-2 py-2 text-left w-28">Style No</th>
                <th className="px-2 py-2 text-left w-24">HSN</th>
                <th className="px-2 py-2 text-right w-20">Qty</th>
                <th className="px-2 py-2 text-left w-16">Unit</th>
                <th className="px-2 py-2 text-right w-24">Rate</th>
                <th className="px-2 py-2 text-right w-16">Disc%</th>
                {!isExport && <th className="px-2 py-2 text-right w-16">GST%</th>}
                <th className="px-2 py-2 text-right w-24">Amount</th>
                {!readOnly && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {(pi.lines || []).length === 0 ? (
                <tr><td colSpan={isExport ? 10 : 11} className="px-3 py-6 text-center text-zinc-500 text-xs">No lines — use Prefill or Add Line.</td></tr>
              ) : pi.lines.map((ln, idx) => {
                const amt = (Number(ln.qty) || 0) * (Number(ln.rate) || 0) * (1 - (Number(ln.discount_pct) || 0) / 100);
                return (
                  <tr key={idx} className="border-t border-[var(--border)]">
                    {/* Item No. = AUTO read-only document line sequence (001/002…)
                        like New Sales Order. Style No. editable + Product-Master
                        auto-pickup. Product Code picker. Description removed from
                        view (line.description preserved in state/payload). Shared
                        by Domestic + Export (Phase 1C-Rev2 / Phase 1D-EPI). */}
                    <td className="px-2 py-1 w-16 align-top"><div data-testid={`${tid}-line-item-${idx}`} className="h-7 px-1.5 flex items-center bg-[var(--bg-2)] border border-[var(--border)] rounded-sm font-mono text-xs text-zinc-400 select-none" title="Auto-generated document line number">{String(idx + 1).padStart(3, "0")}</div></td>
                    <td className="px-2 py-1 align-top">
                      <input disabled={readOnly} list={`pi-articles-master-${idx}`} value={ln.article_no || ""} onChange={e => pickArticle(idx, e.target.value, ln)} placeholder="Pick from master or type" className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono" />
                      <datalist id={`pi-articles-master-${idx}`}>{articles.map(a => (<option key={a.id} value={a.article_no}>{a.description || ""} {a.hsn_code ? `· HSN ${a.hsn_code}` : ""}</option>))}</datalist>
                    </td>
                    <td className="px-2 py-1 w-28 align-top"><input data-testid={`${tid}-line-style-${idx}`} disabled={readOnly} value={ln.style_no || (readOnly ? (savedStyles[idx] || "") : "")} onChange={e => patchLine(idx, { style_no: e.target.value })} placeholder={savedStyles[idx] || "Style No."} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono" /></td>
                    <td className="px-2 py-1"><input disabled={readOnly} value={ln.hsn_code || ""} onChange={e => patchLine(idx, { hsn_code: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} value={ln.qty ?? 0} onChange={e => patchLine(idx, { qty: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input disabled={readOnly} value={ln.unit || ""} onChange={e => patchLine(idx, { unit: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} value={ln.rate ?? 0} onChange={e => patchLine(idx, { rate: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} value={ln.discount_pct ?? 0} onChange={e => patchLine(idx, { discount_pct: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    {!isExport && <td className="px-2 py-1"><input data-testid={`${tid}-line-gst-${idx}`} type="number" step="0.01" disabled={readOnly} value={ln.gst_rate ?? (Number(pi.charges?.tax_pct) || 5)} onChange={e => patchLine(idx, { gst_rate: e.target.value === "" ? "" : Number(e.target.value) })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>}
                    <td className="px-2 py-1 text-right font-mono tabular-nums font-semibold">{fmt(amt)}</td>
                    {!readOnly && <td className="px-2 py-1 text-right"><button title="Remove" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-300"><TrashIcon size={12} /></button></td>}
                  </tr>
                );
              })}
            </tbody>
            {(pi.lines || []).length > 0 && (
              <tfoot className="bg-[var(--bg-2)]/40 border-t-2 border-[var(--border)]">
                <tr className="font-semibold">
                  <td colSpan={4} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500">Totals</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-white" data-testid={`${tid}-totals-qty`}>{Number((pi.lines || []).reduce((a, l) => a + (Number(l.qty) || 0), 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  {!isExport && <td></td>}
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-white" data-testid={`${tid}-totals-amount`}>{fmt((pi.lines || []).reduce((a, l) => a + ((Number(l.qty) || 0) * (Number(l.rate) || 0) * (1 - (Number(l.discount_pct) || 0) / 100)), 0))}</td>
                  {!readOnly && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded-sm p-3 space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Extra Charges</div>
            <NumRow label="Freight" disabled={readOnly} value={pi.charges?.freight} onChange={v => setPi(prev => ({ ...prev, charges: { ...prev.charges, freight: v } }))} />
            <NumRow label="Insurance" disabled={readOnly} value={pi.charges?.insurance} onChange={v => setPi(prev => ({ ...prev, charges: { ...prev.charges, insurance: v } }))} />
            <NumRow label="Packing" disabled={readOnly} value={pi.charges?.packing} onChange={v => setPi(prev => ({ ...prev, charges: { ...prev.charges, packing: v } }))} />
            <NumRow label="GST % (Header default)" disabled={readOnly} value={pi.charges?.tax_pct} onChange={v => setPi(prev => ({ ...prev, charges: { ...prev.charges, tax_pct: v } }))} />
            {!readOnly && !isExport && (
              <button data-testid={`${tid}-apply-tax-all`} onClick={() => setPi(prev => ({
                ...prev, lines: (prev.lines || []).map(l => ({ ...l, gst_rate: Number(prev.charges?.tax_pct) || 0 })),
              }))} className="w-full h-7 mt-1 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-emerald-500/10">
                Apply header GST% to every line
              </button>
            )}
            <div className="pt-2 border-t border-[var(--border)]">
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Delivery Terms</label>
              <input disabled={readOnly} value={pi.delivery_terms || ""} onChange={e => setPi(prev => ({ ...prev, delivery_terms: e.target.value }))} className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
            </div>
            {/* Feb 2026 · T&C template picker + editor. */}
            <div className="pt-2 border-t border-[var(--border)]">
              <div className="flex items-center gap-2 mb-1">
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex-1">Terms &amp; Conditions</label>
                <select
                  data-testid={`${tid}-tnc-template`}
                  disabled={readOnly}
                  value={pi.tnc_template_id || ""}
                  onChange={e => {
                    const id = e.target.value;
                    const tpl = tncTemplates.find(t => t.id === id);
                    setPi(prev => ({ ...prev, tnc_template_id: id,
                      terms_and_conditions: tpl ? tpl.text : (prev.terms_and_conditions || "") }));
                  }}
                  className="h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-[11px]"
                >
                  <option value="">— Select template —</option>
                  {tncTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <textarea rows={4} disabled={readOnly} data-testid={`${tid}-tnc-text`}
                value={pi.terms_and_conditions || ""}
                onChange={e => setPi(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                placeholder="Pick a template or type your own — printed above the bank details."
                className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-sm p-3 space-y-1.5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Totals</div>
            <TotRow label="Subtotal" value={totals.subtotal} />
            <TotRow label="Freight" value={totals.freight} />
            <TotRow label="Insurance" value={totals.insurance} />
            <TotRow label="Packing" value={totals.packing} />
            {/* Mar 2026 · GST parity with Tax Invoice — show CGST+SGST for
                intra-state or IGST for inter-state, computed per-line. */}
            {pi.currency === "INR" && totals.tax_type === "intra" && (totals.total_cgst + totals.total_sgst) > 0 && (
              <>
                <TotRow label="CGST" value={totals.total_cgst} testId={`${tid}-tot-cgst`} />
                <TotRow label="SGST" value={totals.total_sgst} testId={`${tid}-tot-sgst`} />
              </>
            )}
            {pi.currency === "INR" && totals.tax_type === "inter" && totals.total_igst > 0 && (
              <TotRow label="IGST" value={totals.total_igst} testId={`${tid}-tot-igst`} />
            )}
            {pi.currency !== "INR" && totals.tax_amt > 0 && (
              <TotRow label={`Est. Tax (${totals.tax_pct}%)`} value={totals.tax_amt} />
            )}
            <TotRow label="Round Off" value={totals.round_off} signed />
            <div className="border-t border-[var(--border)] pt-1.5 flex items-center justify-between font-mono">
              <span className="text-[11px] uppercase tracking-widest text-zinc-300">Grand Total</span>
              <span className="text-lg font-bold text-[var(--accent)]" data-testid={`${tid}-tot-grand`}>{fmtCurrency(totals.grand_total, pi.currency)}</span>
            </div>
            {pi.currency === "INR" && !isExport && (
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 pt-1 text-right">
                {totals.tax_type === "intra" ? "Intra-state · CGST + SGST" : "Inter-state · IGST"}
              </div>
            )}
          </div>
        </div>

        {(pi.amendments || []).length > 0 && (
          <div className="border border-amber-500/30 rounded-sm p-3 bg-amber-500/5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-2"><WarningIcon size={12} weight="bold" /> Amendment history</div>
            {pi.amendments.map((a, i) => (
              <div key={i} className="text-[11px] text-zinc-300 border-b border-amber-500/10 last:border-0 py-1">
                <span className="text-amber-300 font-mono">{fmtDateTimeIST(a.at)}</span>{" · "}<span className="text-zinc-400">{a.by}</span>{" · "}{a.reason}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <button data-testid={`${tid}-close`} onClick={onClose} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Close</button>
          {initial?.id && <DocDownloadButtons type={isExport ? "EPI" : "PI"} id={initial.id} testPrefix={`${tid}-view`} />}
          {/* DP-PI-PENDING-FREEZE · Phase 2 · Feb 2026 — Domestic PI is
              locked while approval_status == "Pending Approval". Banner
              renders even though the Save/Submit/Issue block below is
              hidden (readOnly), so the operator sees a clear
              explanation for the missing controls. Backend remains
              authoritative. */}
          {initial && isPendingDomesticPi(initial) && (
            <div data-testid={`${tid}-locked-pending-auth`} className="h-9 px-3 flex items-center border border-amber-500/40 bg-amber-500/5 text-amber-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
              Pending Management Authorisation — direct edit / delete locked. Ask Management to Authorise or Reject.
            </div>
          )}
          {/* DP-EPI-PENDING-FREEZE · Phase 2 · Feb 2026 — Parallel banner
              for Export PI Pending Approval. Distinct test-id via `${tid}`
              which resolves to `epi` for exports. */}
          {initial && isPendingExportPi(initial) && (
            <div data-testid={`${tid}-locked-pending-auth`} className="h-9 px-3 flex items-center border border-amber-500/40 bg-amber-500/5 text-amber-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
              Pending Management Authorisation — direct edit / delete locked. Ask Management to Authorise or Reject.
            </div>
          )}
          {!readOnly && !isAmendable && (
            <>
              <button data-testid={`${tid}-save`} disabled={busy} onClick={save} className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">Save as Draft</button>
              {/* Phase 1B.1 · Submit for Authorisation is always rendered on
                  New/Draft/Rejected so the operator can see the next-step
                  action. Disabled with tooltip when mandatory submission
                  requirements are incomplete. */}
              {showSubmit && (
                <button
                  data-testid={`${tid}-submit-approval`}
                  disabled={busy || !enableSubmit}
                  onClick={submitForApproval}
                  title={!enableSubmit ? submitDisabledReason : ""}
                  className="h-9 px-4 bg-sky-500 hover:bg-sky-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
                >
                  {pi.approval_status === "Rejected" ? "Resubmit for Authorisation" : "Submit for Authorisation"}
                </button>
              )}
              {isPending && (
                <div className="h-9 px-3 flex items-center border border-amber-500/40 bg-amber-500/5 text-amber-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
                  Awaiting Authorisation — use the Authorisation panel above
                </div>
              )}
              {/* Phase 1B.1 · Issue Proforma rendered ONLY after Authorisation.
                  Backend /issue endpoint still fail-closed. */}
              {isAuthorised && (
                <button
                  data-testid={`${tid}-issue`}
                  disabled={busy || !canIssue}
                  onClick={issue}
                  title={!canIssue ? "Add buyer and at least one line before issuing" : ""}
                  className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
                >
                  <CheckCircleIcon size={12} weight="bold" /> Issue Proforma
                </button>
              )}
            </>
          )}
          {isAmendable && (
            <button data-testid={`${tid}-amend`} disabled={busy} onClick={amend} className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><PencilLineIcon size={12} weight="bold" /> Save Changes (Amend)</button>
          )}
          {initial?.invoice_no && (
            <button data-testid={`${tid}-editor-print`} disabled={busy} onClick={async () => {
              // Mar 2026 · Editor Print now routes to the same A4 HTML flow
              // used from the grid — Print/PDF (ReportLab) button was removed
              // to keep a single canonical output format.
              const res = await api.get(`/proforma-invoices/${initial.id}/print.html`, { responseType: "text" });
              const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
              const url  = URL.createObjectURL(blob);
              window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }} className="h-9 px-4 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><PrinterIcon size={12} weight="bold" /> Browser Print</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, span }) {
  const colSpan = span === 2 ? "col-span-2" : span === 3 ? "col-span-3" : span === 4 ? "col-span-4" : "";
  return (
    <div className={colSpan}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
function Party({ title, party }) {
  const isBuyer = title === "Buyer";
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{title}</div>
      <div className="text-sm text-white font-semibold">{party?.name || "—"}</div>
      <div className="text-[11px] text-zinc-400 whitespace-pre-line">{party?.address || party?.billing_address || "—"}</div>
      <div className="text-[11px] text-zinc-300 mt-1">
        {party?.country ? <><span className="text-zinc-500">Country:</span> {party.country} · </> : null}
        <span className="text-zinc-500">GSTIN:</span> {party?.gstin || "—"}
      </div>
      {isBuyer && (party?.payment_terms || party?.credit_days) && (
        <div className="text-[11px] mt-2 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 font-mono">{party?.payment_terms || "—"}</span>
          {party?.credit_days ? <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 font-mono">Credit {party.credit_days} days</span> : null}
        </div>
      )}
    </div>
  );
}

// Feb 2026 · Bill To / Ship To editor card. Read-only mirrors the
// display used by <Party>; edit mode swaps in tight inputs so users
// can override the address, country and GSTIN per Proforma.
function PartyEditable({ title, party, readOnly, testId, hint, onChange, onCopyFromBill, showPayment }) {
  const inputCls = "w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs";
  const areaCls  = "w-full min-h-[48px] px-1.5 py-1 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs resize-y";
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{title}</div>
        {!readOnly && onCopyFromBill && (
          <button
            type="button"
            onClick={onCopyFromBill}
            data-testid={`${testId}-copy-from-bill`}
            className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
          >
            Same as Bill To
          </button>
        )}
      </div>
      {readOnly ? (
        <>
          <div className="text-sm text-white font-semibold">{party?.name || "—"}</div>
          <div className="text-[11px] text-zinc-400 whitespace-pre-line">{party?.address || "—"}</div>
          <div className="text-[11px] text-zinc-300">
            {party?.country ? <><span className="text-zinc-500">Country:</span> {party.country} · </> : null}
            <span className="text-zinc-500">GSTIN:</span> {party?.gstin || "—"}
          </div>
        </>
      ) : (
        <>
          <input
            value={party?.name || ""}
            onChange={e => onChange({ ...party, name: e.target.value })}
            placeholder="Party name"
            className={`${inputCls} font-semibold text-white`}
            data-testid={`${testId}-name`}
          />
          <textarea
            value={party?.address || ""}
            onChange={e => onChange({ ...party, address: e.target.value })}
            placeholder="Address (multi-line)"
            className={areaCls}
            data-testid={`${testId}-address`}
          />
          <div className="grid grid-cols-3 gap-1.5">
            <input
              value={party?.city || ""}
              onChange={e => onChange({ ...party, city: e.target.value })}
              placeholder="City"
              className={inputCls}
              data-testid={`${testId}-city`}
            />
            <input
              value={party?.state || ""}
              onChange={e => onChange({ ...party, state: e.target.value })}
              placeholder="State"
              className={inputCls}
              data-testid={`${testId}-state`}
            />
            <input
              value={party?.pincode || ""}
              onChange={e => onChange({ ...party, pincode: e.target.value })}
              placeholder="Zip / PIN"
              className={`${inputCls} font-mono`}
              data-testid={`${testId}-pincode`}
            />
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <input
              value={party?.country || ""}
              onChange={e => onChange({ ...party, country: e.target.value })}
              placeholder="Country"
              className={inputCls}
              data-testid={`${testId}-country`}
            />
            <input
              value={party?.state_code || ""}
              onChange={e => onChange({ ...party, state_code: e.target.value })}
              placeholder="State Code"
              className={`${inputCls} font-mono`}
              data-testid={`${testId}-state-code`}
            />
            <input
              value={party?.gstin || ""}
              onChange={e => onChange({ ...party, gstin: e.target.value.toUpperCase() })}
              placeholder="GSTIN"
              className={`${inputCls} font-mono uppercase`}
              data-testid={`${testId}-gstin`}
            />
          </div>
        </>
      )}
      {hint && <div className="text-[10px] text-zinc-500 italic mt-1">{hint}</div>}
      {showPayment && (party?.payment_terms || party?.credit_days) && (
        <div className="text-[11px] mt-2 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 font-mono">{party?.payment_terms || "—"}</span>
          {party?.credit_days ? <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 font-mono">Credit {party.credit_days} days</span> : null}
        </div>
      )}
    </div>
  );
}
function NumRow({ label, value, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex-1">{label}</label>
      <input type="number" step="0.01" disabled={disabled} value={value ?? 0} onChange={e => onChange(e.target.value)} className="w-32 h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" />
    </div>
  );
}
function TotRow({ label, value, signed, testId }) {
  const v = Number(value) || 0;
  const prefix = signed && v > 0 ? "+" : "";
  return (
    <div className="flex items-center justify-between text-[11px] font-mono" data-testid={testId}>
      <span className="text-zinc-500 uppercase tracking-widest">{label}</span>
      <span className="tabular-nums text-zinc-200">{prefix}{fmt(value)}</span>
    </div>
  );
}
